/**
 * Mock session service — wraps the in-memory session helpers and records the
 * mock audit log for terminations (mock mode only).
 */

import {
  getSessionsForUser,
  getAllActiveSessions,
  terminateSession as terminateMockSession,
  terminateAllUserSessions,
  getSessionStats,
} from '@/data/mockSessions'
import { addAuditEntry } from '@/data/mockAuditLog'
import { mockDelay } from '@/services/factory'
import type { SessionService, SessionStats } from './sessions.service'
import type { UserSession } from '@/types/session.types'

export function createMockSessionService(): SessionService {
  return {
    async listForUser(userId: string): Promise<UserSession[]> {
      await mockDelay(300)
      return getSessionsForUser(userId)
    },

    async listAllActive(): Promise<UserSession[]> {
      await mockDelay(300)
      return getAllActiveSessions()
    },

    async getStats(): Promise<SessionStats> {
      await mockDelay(200)
      return getSessionStats()
    },

    async terminate(sessionId: string): Promise<boolean> {
      await mockDelay(300)
      const session = getAllActiveSessions().find(s => s.sessionId === sessionId)
      const success = terminateMockSession(sessionId)
      if (success) {
        addAuditEntry({
          action: 'session_terminated',
          resourceType: 'session',
          resourceId: sessionId,
          details: { sessionId, terminatedUserId: session?.userId ?? null },
          success: true,
        })
      }
      return success
    },

    async terminateAllOther(userId: string, currentSessionId: string): Promise<number> {
      await mockDelay(500)
      const count = terminateAllUserSessions(userId, currentSessionId)
      addAuditEntry({
        action: 'all_sessions_terminated',
        resourceType: 'session',
        details: { terminatedSessionCount: count, reason: 'User requested' },
        success: true,
      })
      return count
    },
  }
}
